/**
 * Validation utility functions
 * Leverages common validation functions from @/utils/device/commonValidation
 */
import type { TFunction } from 'i18next';
import { i18nKeys } from '@/i18n/i18nKeyGen';
import type { ConstraintInfo } from '../types/validation';

/**
 * Constraint를 UI 값으로 변환 (ranges 방식으로 통일)
 */
export const convertConstraintToCurrentUnit = (constraint: any): any => {
  if (!constraint) return {};

  return Object.keys(constraint).reduce((acc: any, key) => {
    const constraintInfo = constraint[key];

    if (constraintInfo?.ranges) {
      const scale = constraintInfo.scale || 1;
      acc[key] = {
        ...constraintInfo,
        ranges: constraintInfo.ranges.map(([min, max]: [number, number]) => [
          min / scale,
          max / scale,
        ]),
        default: constraintInfo.default ? constraintInfo.default / scale : undefined,
      };
    } else {
      acc[key] = constraintInfo;
    }

    return acc;
  }, {});
};

/**
 * 통일된 힌트 생성 함수 (ranges + unit)
 */
export const makeHint = (
  hintKey: string,
  constraintInfo: ConstraintInfo,
  t: TFunction
): string | undefined => {
  if (!constraintInfo?.ranges || constraintInfo.ranges.length === 0) {
    return undefined;
  }

  const unit = constraintInfo.unit ? ` ${constraintInfo.unit}` : '';

  if (constraintInfo.ranges.length === 1) {
    const [min, max] = constraintInfo.ranges[0];
    return t(hintKey, {
      min,
      max,
      unit,
      range: `${min} ~ ${max}${unit}`,
    });
  } else {
    const rangeStrings = constraintInfo.ranges
      .map((range: number[]) => {
        const [min, max] = range;
        return `${min}~${max}`;
      })
      .join(t(i18nKeys.config.rangeSeparator));
    return t(hintKey, {
      ranges: rangeStrings,
      unit,
      range: `${rangeStrings}${unit}`,
    });
  }
};

/**
 * 기본 필드 유효성 검사 함수
 */
export const validateFieldValue = (
  fieldName: string,
  value: any,
  constraintInfo: ConstraintInfo | undefined,
  t: TFunction
): string => {
  // 기본값 검증 - 빈값 체크 (undefined는 기본값이 있어도 사용자가 입력하지 않은 경우이므로 에러 표시)
  if (
    value === null ||
    value === undefined ||
    value === '' ||
    (typeof value === 'number' && isNaN(value))
  ) {
    return t(i18nKeys.config.error_valueRequired);
  }

  // Constraint 기반 검증 (센서 설정용)
  if (constraintInfo?.ranges && typeof value === 'number') {
    const isValidInAnyRange = constraintInfo.ranges.some((range: number[]) => {
      const [min, max] = range;
      return value >= min && value <= max;
    });

    if (!isValidInAnyRange) {
      if (constraintInfo.ranges.length === 1) {
        const [min, max] = constraintInfo.ranges[0];
        return t(i18nKeys.config.error_rangeValue, { fieldName, min, max });
      } else {
        const rangeStrings = constraintInfo.ranges
          .map((range: number[]) => {
            const [min, max] = range;
            return `${min}~${max}`;
          })
          .join(t(i18nKeys.config.rangeSeparator));
        return t(i18nKeys.config.error_rangeValues, { fieldName, ranges: rangeStrings });
      }
    }
    return '';
  }

  return '';
};
